import { useEffect, useRef, useState } from 'react';
import styles from '../MaterialPreviewModal.module.css';
import { Z_INDEX } from '../../../../styles/zIndex';

export type CropState = { x: number; y: number; width: number; height: number };

export type AspectRatio =
  | 'custom'
  | '1:1'
  | '4:3'
  | '16:9'
  | '9:16'
  | '3:4'
  | '2:3'
  | '3:2'
  | '4:5'
  | '1.91:1'
  | '1:1.91';

type Props = {
  imageUrl: string;
  aspectRatio: AspectRatio;
  paddingMode: 'none' | 'white' | 'transparent';
  crop: CropState;
  zoom: number;
  onUpdateCrop: (crop: CropState) => void;
  onInteractionStart?: () => void;
  onInteractionEnd?: () => void;
  onZoomStep?: (direction: number) => void;
  onImageLoad?: (naturalWidth: number, naturalHeight: number) => void;
  /** 表示時の最大幅（スマホ用）。指定すると画像をこの幅に収まるようスケール */
  maxDisplayWidth?: number;
  /** 表示時の最大高さ（スマホ用）。指定すると画像をこの高さに収まるようスケール */
  maxDisplayHeight?: number;
};

type DragMode = 'move' | 'resize';
type ResizeCorner = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

type DragState = {
  mode: DragMode;
  startX: number;
  startY: number;
  startCropX: number;
  startCropY: number;
  startCropWidth: number;
  startCropHeight: number;
  resizeCorner?: ResizeCorner;
};

const MIN_CROP_SIZE = 0.05;

const clamp = (value: number, min = 0, max = 1) => Math.min(Math.max(value, min), max);

const getAspectRatioValue = (ratio: AspectRatio): number | null => {
  switch (ratio) {
    case '1:1':
      return 1;
    case '4:3':
      return 4 / 3;
    case '16:9':
      return 16 / 9;
    case '9:16':
      return 9 / 16;
    case '3:4':
      return 3 / 4;
    case '2:3':
      return 2 / 3;
    case '3:2':
      return 3 / 2;
    case '4:5':
      return 4 / 5;
    case '1.91:1':
      return 1.91;
    case '1:1.91':
      return 1 / 1.91;
    case 'custom':
    default:
      return null;
  }
};

export function ImageEditCanvas({
  imageUrl,
  aspectRatio,
  paddingMode,
  crop,
  zoom,
  onUpdateCrop,
  onInteractionStart,
  onInteractionEnd,
  onZoomStep,
  onImageLoad,
  maxDisplayWidth,
  maxDisplayHeight,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const [selection, setSelection] = useState<CropState>(() => crop ?? { x: 0, y: 0, width: 1, height: 1 });
  const dragState = useRef<DragState | null>(null);
  const isInitialMount = useRef(true);
  // リサイズ中の画像画面位置固定用
  const [resizeState, setResizeState] = useState<{
    cropCenterX: number; // リサイズ開始時のクロップ中心(正規化座標)
    cropCenterY: number;
    wrapperWidth: number; // リサイズ開始時のwrapperサイズ(px)
    wrapperHeight: number;
  } | null>(null);

  const prevSelectionRef = useRef<CropState>(selection);
  const updateCropTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // 選択範囲が実際に変更されたときのみ通知（デバウンス付き）
    const prev = prevSelectionRef.current;
    const isSame =
      Math.abs(selection.x - prev.x) < 0.0001 &&
      Math.abs(selection.y - prev.y) < 0.0001 &&
      Math.abs(selection.width - prev.width) < 0.0001 &&
      Math.abs(selection.height - prev.height) < 0.0001;
    
    if (!isSame) {
      prevSelectionRef.current = selection;
      
      // 既存のタイマーをクリア
      if (updateCropTimeoutRef.current) {
        clearTimeout(updateCropTimeoutRef.current);
      }
      
      // デバウンス処理
      updateCropTimeoutRef.current = setTimeout(() => {
        onUpdateCrop(selection);
      }, 16); // 約1フレーム分遅延
    }
  }, [selection, onUpdateCrop]);

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (updateCropTimeoutRef.current) {
        clearTimeout(updateCropTimeoutRef.current);
      }
    };
  }, []);

  const prevCropRef = useRef<CropState | null>(crop);

  useEffect(() => {
    if (!crop) {
      return;
    }
    
    // 前回のcropと比較
    const prevCrop = prevCropRef.current;
    if (prevCrop) {
      const cropChanged =
        Math.abs(crop.x - prevCrop.x) > 0.0001 ||
        Math.abs(crop.y - prevCrop.y) > 0.0001 ||
        Math.abs(crop.width - prevCrop.width) > 0.0001 ||
        Math.abs(crop.height - prevCrop.height) > 0.0001;
      
      if (!cropChanged) {
        return;
      }
    }
    
    prevCropRef.current = crop;
    
    const isSame =
      Math.abs(selection.x - crop.x) < 0.0001 &&
      Math.abs(selection.y - crop.y) < 0.0001 &&
      Math.abs(selection.width - crop.width) < 0.0001 &&
      Math.abs(selection.height - crop.height) < 0.0001;
    if (!isSame) {
      setSelection(crop);
    }
  }, [crop, selection.x, selection.y, selection.width, selection.height]);

  // アスペクト比が変更されたら、枠のサイズを計算（位置は常に中央固定）
  const prevAspectRatioRef = useRef<AspectRatio>(aspectRatio);
  
  useEffect(() => {
    // 初回マウント時はスキップ
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // アスペクト比が実際に変更されたときのみ実行
    if (prevAspectRatioRef.current === aspectRatio) {
      return;
    }
    prevAspectRatioRef.current = aspectRatio;

    if (!imageDimensions) {
      return;
    }

    const targetAspect = getAspectRatioValue(aspectRatio);
    if (targetAspect === null) {
      // カスタムの場合は現在の選択を維持
      return;
    }

    const imageAspect = imageDimensions.width / imageDimensions.height;

    let newCrop: CropState;

    if (imageAspect > targetAspect) {
      // 画像が横長 → 左右をクロップ
      const newWidth = targetAspect / imageAspect;
      const newX = (1 - newWidth) / 2; // 中央に配置
      newCrop = {
        x: newX,
        y: 0,
        width: newWidth,
        height: 1,
      };
    } else {
      // 画像が縦長 → 上下をクロップ
      const newHeight = imageAspect / targetAspect;
      const newY = (1 - newHeight) / 2; // 中央に配置
      newCrop = {
        x: 0,
        y: newY,
        width: 1,
        height: newHeight,
      };
    }

    setSelection(newCrop);
  }, [aspectRatio, imageDimensions]);

  const paddingStyle: React.CSSProperties = (() => {
    if (paddingMode === 'white') {
      return { background: '#ffffff' };
    }
    if (paddingMode === 'transparent') {
      return { background: 'rgba(255, 255, 255, 0.0)' };
    }
    return { background: 'rgba(0, 0, 0, 0.0)' };
  })();

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    onImageLoad?.(img.naturalWidth, img.naturalHeight);
  };

  const getRelativePosition = (event: React.MouseEvent) => {
    const containerBounds = containerRef.current?.getBoundingClientRect();
    if (!containerBounds) {
      return null;
    }
    // コンテナに対する相対位置（0-1の正規化座標）
    const x = (event.clientX - containerBounds.left) / containerBounds.width;
    const y = (event.clientY - containerBounds.top) / containerBounds.height;
    return { x, y };
  };

  useEffect(() => {
    const handlePointerMove = (clientX: number, clientY: number) => {
      if (!dragState.current || !containerRef.current || !imageDimensions) return;

      const containerBounds = containerRef.current.getBoundingClientRect();
      const x = (clientX - containerBounds.left) / containerBounds.width;
      const y = (clientY - containerBounds.top) / containerBounds.height;

      if (dragState.current.mode === 'move') {
        const damping = 0.5;
        const dx = (x - dragState.current.startX) * damping;
        const dy = (y - dragState.current.startY) * damping;
        const startCropX = dragState.current.startCropX;
        const startCropY = dragState.current.startCropY;

        setSelection((prev) => ({
          ...prev,
          x: clamp(startCropX - dx, 0, 1 - prev.width),
          y: clamp(startCropY - dy, 0, 1 - prev.height),
        }));
      } else if (dragState.current.mode === 'resize') {
        const corner = dragState.current.resizeCorner!;
        const { startCropX, startCropY, startCropWidth, startCropHeight } = dragState.current;
        const dx = x - dragState.current.startX;
        const dy = y - dragState.current.startY;

        // 対角の頂点をアンカーとして枠サイズを変更
        let newWidth: number, newHeight: number, newX: number, newY: number;

        switch (corner) {
          case 'bottomRight':
            newWidth = startCropWidth + dx;
            newHeight = startCropHeight + dy;
            newX = startCropX;
            newY = startCropY;
            break;
          case 'topLeft':
            newWidth = startCropWidth - dx;
            newHeight = startCropHeight - dy;
            newX = startCropX + dx;
            newY = startCropY + dy;
            break;
          case 'topRight':
            newWidth = startCropWidth + dx;
            newHeight = startCropHeight - dy;
            newX = startCropX;
            newY = startCropY + dy;
            break;
          case 'bottomLeft':
            newWidth = startCropWidth - dx;
            newHeight = startCropHeight + dy;
            newX = startCropX + dx;
            newY = startCropY;
            break;
        }

        // アスペクト比の維持（常に固定）
        // customの場合は現在の枠の比率を維持
        const normalizedRatio = startCropWidth / startCropHeight;
        const absDw = Math.abs(newWidth - startCropWidth);
        const absDh = Math.abs(newHeight - startCropHeight);

        if (absDw >= absDh) {
          newHeight = newWidth / normalizedRatio;
        } else {
          newWidth = newHeight * normalizedRatio;
        }

        // アスペクト比調整後、固定点からx,yを再計算
        switch (corner) {
          case 'topLeft':
            newX = startCropX + startCropWidth - newWidth;
            newY = startCropY + startCropHeight - newHeight;
            break;
          case 'topRight':
            newY = startCropY + startCropHeight - newHeight;
            break;
          case 'bottomLeft':
            newX = startCropX + startCropWidth - newWidth;
            break;
          case 'bottomRight':
            break;
        }

        // 最小サイズ制約
        if (newWidth < MIN_CROP_SIZE) {
          const ratio = MIN_CROP_SIZE / newWidth;
          newWidth = MIN_CROP_SIZE;
          newHeight *= ratio;
        }
        if (newHeight < MIN_CROP_SIZE) {
          const ratio = MIN_CROP_SIZE / newHeight;
          newHeight = MIN_CROP_SIZE;
          newWidth *= ratio;
        }

        // 画像範囲内にクランプ
        newWidth = Math.min(newWidth, 1);
        newHeight = Math.min(newHeight, 1);
        newX = clamp(newX, 0, 1 - newWidth);
        newY = clamp(newY, 0, 1 - newHeight);

        setSelection({ x: newX, y: newY, width: newWidth, height: newHeight });
      }
    };

    const handleGlobalMouseMove = (event: MouseEvent) => {
      handlePointerMove(event.clientX, event.clientY);
    };

    const handleGlobalMouseUp = () => {
      if (dragState.current) {
        document.body.style.cursor = '';
        if (dragState.current.mode === 'resize') setResizeState(null);
        dragState.current = null;
        onInteractionEnd?.();
      }
    };

    const handleGlobalTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (!touch) return;
      handlePointerMove(touch.clientX, touch.clientY);
    };

    const handleGlobalTouchEnd = () => {
      if (dragState.current) {
        document.body.style.cursor = '';
        if (dragState.current.mode === 'resize') setResizeState(null);
        dragState.current = null;
        onInteractionEnd?.();
      }
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('touchmove', handleGlobalTouchMove, { passive: true });
    window.addEventListener('touchend', handleGlobalTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('touchmove', handleGlobalTouchMove);
      window.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [imageDimensions, aspectRatio, onInteractionEnd]);

  const startDrag = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault(); // ブラウザのデフォルト動作を防ぐ
    const pos = getRelativePosition(event);
    if (!pos) {
      return;
    }
    dragState.current = {
      mode: 'move',
      startX: pos.x,
      startY: pos.y,
      startCropX: selection.x,
      startCropY: selection.y,
      startCropWidth: selection.width,
      startCropHeight: selection.height,
    };
    onInteractionStart?.();
  };

  const startTouchDrag = (event: React.TouchEvent<HTMLDivElement>) => {
    const touch = event.touches[0];
    if (!touch || !containerRef.current) {
      return;
    }
    const containerBounds = containerRef.current.getBoundingClientRect();
    const x = (touch.clientX - containerBounds.left) / containerBounds.width;
    const y = (touch.clientY - containerBounds.top) / containerBounds.height;
    dragState.current = {
      mode: 'move',
      startX: x,
      startY: y,
      startCropX: selection.x,
      startCropY: selection.y,
      startCropWidth: selection.width,
      startCropHeight: selection.height,
    };
    onInteractionStart?.();
  };

  const captureResizeState = () => {
    if (!containerRef.current) return;
    setResizeState({
      cropCenterX: selection.x + selection.width / 2,
      cropCenterY: selection.y + selection.height / 2,
      wrapperWidth: containerRef.current.clientWidth,
      wrapperHeight: containerRef.current.clientHeight,
    });
  };

  const startResize = (event: React.MouseEvent, corner: ResizeCorner) => {
    event.preventDefault();
    event.stopPropagation();
    const pos = getRelativePosition(event);
    if (!pos) return;
    captureResizeState();
    const cursorMap: Record<ResizeCorner, string> = {
      topLeft: 'nwse-resize',
      topRight: 'nesw-resize',
      bottomLeft: 'nesw-resize',
      bottomRight: 'nwse-resize',
    };
    document.body.style.cursor = cursorMap[corner];
    dragState.current = {
      mode: 'resize',
      startX: pos.x,
      startY: pos.y,
      startCropX: selection.x,
      startCropY: selection.y,
      startCropWidth: selection.width,
      startCropHeight: selection.height,
      resizeCorner: corner,
    };
    onInteractionStart?.();
  };

  const startTouchResize = (event: React.TouchEvent, corner: ResizeCorner) => {
    event.stopPropagation();
    const touch = event.touches[0];
    if (!touch || !containerRef.current) return;
    captureResizeState();
    const containerBounds = containerRef.current.getBoundingClientRect();
    const x = (touch.clientX - containerBounds.left) / containerBounds.width;
    const y = (touch.clientY - containerBounds.top) / containerBounds.height;
    dragState.current = {
      mode: 'resize',
      startX: x,
      startY: y,
      startCropX: selection.x,
      startCropY: selection.y,
      startCropWidth: selection.width,
      startCropHeight: selection.height,
      resizeCorner: corner,
    };
    onInteractionStart?.();
  };

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (!onZoomStep) {
      return;
    }
    event.preventDefault();
    onZoomStep(event.deltaY < 0 ? 1 : -1);
  };

  // 表示スケール計算（maxDisplayWidth/maxDisplayHeightが指定されている場合）
  const displayScale = (() => {
    if ((!maxDisplayWidth && !maxDisplayHeight) || !imageDimensions) return 1;
    const cropWidth = selection.width * imageDimensions.width;
    const cropHeight = selection.height * imageDimensions.height;

    let scaleX = 1;
    let scaleY = 1;

    if (maxDisplayWidth && cropWidth > maxDisplayWidth) {
      scaleX = maxDisplayWidth / cropWidth;
    }
    if (maxDisplayHeight && cropHeight > maxDisplayHeight) {
      scaleY = maxDisplayHeight / cropHeight;
    }

    // 両方の制約を満たす小さい方のスケールを使用
    return Math.min(scaleX, scaleY);
  })();

  // 表示用の統合スケール: displayScale（スマホ用）× zoom（PC用）
  // スマホ（maxDisplayWidth指定時）はdisplayScaleでサイズ調整、zoomは1固定
  const effectiveZoom = maxDisplayWidth ? 1 : zoom;
  const renderScale = displayScale * effectiveZoom;

  // リサイズ中はwrapperサイズを固定し、flex中央配置に切り替え
  const wrapperStyle: React.CSSProperties = {
    ...paddingStyle,
    cursor: 'move',
    overflow: 'visible',
    ...(resizeState ? {
      width: `${resizeState.wrapperWidth}px`,
      height: `${resizeState.wrapperHeight}px`,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    } : {}),
  };

  // クロップ領域を中央に表示するコンテナ
  // リサイズ中はCSS transformで画像位置を補正（クロップ中心のずれを打ち消す）
  const resizeTransform = resizeState && imageDimensions ? (() => {
    const tx = (selection.x + selection.width / 2 - resizeState.cropCenterX) * imageDimensions.width * renderScale;
    const ty = (selection.y + selection.height / 2 - resizeState.cropCenterY) * imageDimensions.height * renderScale;
    return `translate(${tx}px, ${ty}px)`;
  })() : undefined;

  const cropContainerStyle: React.CSSProperties = {
    position: 'relative',
    width: imageDimensions ? `${selection.width * imageDimensions.width * renderScale}px` : 'auto',
    height: imageDimensions ? `${selection.height * imageDimensions.height * renderScale}px` : 'auto',
    overflow: 'visible',
    ...(resizeState ? { transform: resizeTransform } : { margin: 'auto' }),
  };

  // クロップ枠のスタイル - クロップコンテナの境界
  const cropFrameStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    border: '2px solid rgba(255, 215, 128, 0.9)',
    borderRadius: '4px',
    pointerEvents: 'none',
    zIndex: Z_INDEX.panel,
  };

  // 画像全体を含むコンテナ（オーバーレイの基準）
  const imageContainerStyle: React.CSSProperties = {
    position: 'absolute',
    left: imageDimensions ? `${-selection.x * imageDimensions.width * renderScale}px` : 0,
    top: imageDimensions ? `${-selection.y * imageDimensions.height * renderScale}px` : 0,
    width: imageDimensions ? `${imageDimensions.width * renderScale}px` : 'auto',
    height: imageDimensions ? `${imageDimensions.height * renderScale}px` : 'auto',
    pointerEvents: 'none',
    overflow: 'hidden',
  };

  // 調整後の画像スタイル（imageContainer内で原点に配置）
  const adjustedImageStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    height: '100%',
    userSelect: 'none',
    pointerEvents: 'none',
    position: 'relative',
    zIndex: Z_INDEX.raised,
  };

  // 調整後のオーバーレイスタイル
  const adjustedOverlayTopStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: imageDimensions ? `${selection.y * imageDimensions.height * renderScale}px` : 0,
    background: 'rgba(0, 0, 0, 0.5)',
    pointerEvents: 'none',
    zIndex: Z_INDEX.elevated,
  };

  const adjustedOverlayBottomStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: imageDimensions ? `${(selection.y + selection.height) * imageDimensions.height * renderScale}px` : 0,
    width: '100%',
    height: imageDimensions ? `${(1 - selection.y - selection.height) * imageDimensions.height * renderScale}px` : 0,
    background: 'rgba(0, 0, 0, 0.5)',
    pointerEvents: 'none',
    zIndex: Z_INDEX.elevated,
  };

  const adjustedOverlayLeftStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: imageDimensions ? `${selection.y * imageDimensions.height * renderScale}px` : 0,
    width: imageDimensions ? `${selection.x * imageDimensions.width * renderScale}px` : 0,
    height: imageDimensions ? `${selection.height * imageDimensions.height * renderScale}px` : '100%',
    background: 'rgba(0, 0, 0, 0.5)',
    pointerEvents: 'none',
    zIndex: Z_INDEX.elevated,
  };

  const adjustedOverlayRightStyle: React.CSSProperties = {
    position: 'absolute',
    left: imageDimensions ? `${(selection.x + selection.width) * imageDimensions.width * renderScale}px` : 0,
    top: imageDimensions ? `${selection.y * imageDimensions.height * renderScale}px` : 0,
    width: imageDimensions ? `${(1 - selection.x - selection.width) * imageDimensions.width * renderScale}px` : 0,
    height: imageDimensions ? `${selection.height * imageDimensions.height * renderScale}px` : '100%',
    background: 'rgba(0, 0, 0, 0.5)',
    pointerEvents: 'none',
    zIndex: Z_INDEX.elevated,
  };

  // オーバーレイコンテナ用のz-index（画像(raised=2)より上、クロップ枠(panel=10)より下）
  const overlayContainerZIndex = Z_INDEX.elevated;

  return (
    <div
      ref={containerRef}
      className={styles.canvasImageWrapper}
      style={wrapperStyle}
      onMouseDown={startDrag}
      onTouchStart={startTouchDrag}
      onWheel={handleWheel}
    >
      {/* クロップ領域コンテナ（中央配置） */}
      <div style={cropContainerStyle}>
        {/* 画像全体を含むコンテナ（負の座標でクロップ位置に応じて移動） */}
        <div style={imageContainerStyle}>
          {/* 画像 */}
          <img
            ref={imageRef}
            src={imageUrl}
            alt="編集対象"
            className={styles.canvasImage}
            onLoad={handleImageLoad}
            draggable={false}
            style={adjustedImageStyle}
          />
        </div>
        {/* グレーオーバーレイ（クロップ領域外を暗くする） - imageContainerの外に配置 */}
        <div style={{...imageContainerStyle, overflow: 'visible', zIndex: overlayContainerZIndex}}>
          <div style={adjustedOverlayTopStyle} />
          <div style={adjustedOverlayBottomStyle} />
          <div style={adjustedOverlayLeftStyle} />
          <div style={adjustedOverlayRightStyle} />
        </div>
        {/* クロップ枠（クロップコンテナの境界） */}
        <div style={cropFrameStyle} />
        {/* 四隅のリサイズハンドル（L字型コーナーマーク） */}
        <div
          className={`${styles.cropCornerHandle} ${styles.cropCornerTopLeft}`}
          style={{ zIndex: Z_INDEX.panel + 1 }}
          onMouseDown={(e) => startResize(e, 'topLeft')}
          onTouchStart={(e) => startTouchResize(e, 'topLeft')}
        />
        <div
          className={`${styles.cropCornerHandle} ${styles.cropCornerTopRight}`}
          style={{ zIndex: Z_INDEX.panel + 1 }}
          onMouseDown={(e) => startResize(e, 'topRight')}
          onTouchStart={(e) => startTouchResize(e, 'topRight')}
        />
        <div
          className={`${styles.cropCornerHandle} ${styles.cropCornerBottomLeft}`}
          style={{ zIndex: Z_INDEX.panel + 1 }}
          onMouseDown={(e) => startResize(e, 'bottomLeft')}
          onTouchStart={(e) => startTouchResize(e, 'bottomLeft')}
        />
        <div
          className={`${styles.cropCornerHandle} ${styles.cropCornerBottomRight}`}
          style={{ zIndex: Z_INDEX.panel + 1 }}
          onMouseDown={(e) => startResize(e, 'bottomRight')}
          onTouchStart={(e) => startTouchResize(e, 'bottomRight')}
        />
      </div>
    </div>
  );
}
